import React from 'react'
import { IBadgeProps } from './interfaces'
import { TeamOutlined } from '@ant-design/icons'
import { Popover } from 'antd'
import { AppService } from '../../services/index'
import { Container } from 'typescript-ioc/es5'
import { SwLink } from '../sw-link/sw-link'
import './audience-badge.scss'
import { SegmentType } from '@pushly/models/lib/enums/segment-type'

/**
 * Minimal segment shape the badge needs for rendering.
 * Uses camelCase properties matching SegmentDto / Segment from @pushly/models.
 */
export interface AudienceBadgeSegment {
    id: number
    name: string
    type?: SegmentType
    isDefault?: boolean
}

export interface AudienceBadgeAudience {
    segments?: AudienceBadgeSegment[]
    excludedSegments?: AudienceBadgeSegment[]
    subscriberIds?: string[]
    externalSubscriberIds?: string[]
}

interface IProps extends IBadgeProps {
    audience?: AudienceBadgeAudience
}

export class AudienceBadge extends React.PureComponent<IProps> {
    protected appService: AppService

    public constructor(props: IProps) {
        super(props)

        this.appService = Container.get(AppService)
    }

    public render() {
        const { audience, hideTooltip } = this.props

        if (!audience) return <span className="audience-badge" />

        if (Array.isArray(audience.subscriberIds) || Array.isArray(audience.externalSubscriberIds)) {
            return (
                <span className="audience-badge">
                    <span className="included-badge">
                        <span className="included-icon">
                            <TeamOutlined />
                        </span>
                        <span>Subscriber IDs</span>
                    </span>
                </span>
            )
        }

        const segments = audience.segments ?? []
        const excludedSegments = audience.excludedSegments ?? []

        // Detect single "all subscribers" segment (modern types or legacy isDefault flag)
        const allSegment = segments.length === 1
            ? this.detectAllSegment(segments[0])
            : undefined

        const nonDefaultSegments = allSegment ? segments : segments.filter((s) => !s.isDefault)

        const includedBadge: React.ReactNode = nonDefaultSegments.length > 0
            ? allSegment
                ? this.renderSegmentLink(allSegment)
                : (
                    <span className="included-badge">
                        <span className="included-icon"><TeamOutlined /></span>
                        <span>{nonDefaultSegments.length}</span>
                        <span>Included</span>
                    </span>
                )
            : null

        const excludedBadge: React.ReactNode = excludedSegments.length > 0
            ? (
                <span className="excluded-badge">
                    <span className="excluded-icon"><TeamOutlined /></span>
                    <span>{excludedSegments.length}</span>
                    <span>Excluded</span>
                </span>
            )
            : null

        return (
            <span className="audience-badge">
                {includedBadge && (allSegment || hideTooltip
                    ? includedBadge
                    : (
                        <Popover
                            getPopupContainer={() => this.appService.getAppContainer()}
                            overlayClassName="nd-excluded-popover"
                            title="Included Segments"
                            content={nonDefaultSegments.map((s) => (
                                <div key={s.id}>
                                    <SwLink href={this.appService.routeWithinDomain(`/segments/${s.id}/summary`, true)}>
                                        {/^all$/i.test(s.name) ? 'All Subscribers' : s.name}
                                    </SwLink>
                                </div>
                            ))}
                        >
                            {includedBadge}
                        </Popover>
                    )
                )}
                {excludedBadge && (
                    <>
                        <span className="details-divider">|</span>
                        {hideTooltip
                            ? excludedBadge
                            : (
                                <Popover
                                    getPopupContainer={() => this.appService.getAppContainer()}
                                    overlayClassName="nd-excluded-popover"
                                    title="Excluded Segments"
                                    content={excludedSegments.map((s) => (
                                        <div key={s.id}>
                                            <SwLink href={this.appService.routeWithinDomain(`/segments/${s.id}/summary`, true)}>
                                                {s.name}
                                            </SwLink>
                                        </div>
                                    ))}
                                >
                                    {excludedBadge}
                                </Popover>
                            )
                        }
                    </>
                )}
            </span>
        )
    }

    /** Returns the segment if it represents an "all subscribers" variant, otherwise undefined. */
    private detectAllSegment(seg: AudienceBadgeSegment): AudienceBadgeSegment | undefined {
        if (seg.type === SegmentType.ALL_PUSH_SUBSCRIBERS) return seg
        if (seg.type === SegmentType.ALL_APP_USERS) return seg
        if (seg.isDefault) return seg
        return undefined
    }

    /** Renders a linked badge for a single named segment. */
    private renderSegmentLink(segment: AudienceBadgeSegment): React.ReactNode {
        const name = segment.name === 'All' ? 'All Subscribers' : segment.name
        return (
            <span className="included-badge">
                <span className="included-icon"><TeamOutlined /></span>
                <SwLink href={this.appService.routeWithinDomain(`/segments/${segment.id}/summary`, true)}>
                    {name}
                </SwLink>
            </span>
        )
    }
}
